import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

interface AlertCustomProps {
  message: string;
  onClose: () => void;
  type?: 'error' | 'success' | 'warning' | 'info';
}

const slideIn = keyframes`
  from { transform: translateY(-100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const AlertWrapper = styled.div`
  position: fixed;
  top: 20px;
  left: 0;
  right: 0;
  margin: 0 auto;
  z-index: 9999;
  max-width: 400px;
  width: calc(100% - 40px);
  animation: ${slideIn} 0.3s ease-out;
  pointer-events: none;
`;

const AlertContent = styled.div<{ type: string }>`
  padding: 15px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: auto;

  ${({ type }) => {
    switch (type) {
      case 'error':
        return `
          background-color: rgba(253, 236, 234, 0.95);
          color: #611a15;
          border: 1px solid #f5c6cb;
        `;
      case 'success':
        return `
          background-color: rgba(232, 245, 233, 0.95);
          color: #1b5e20;
          border: 1px solid #c8e6c9;
        `;
      case 'warning':
        return `
          background-color: rgba(255, 243, 224, 0.95);
          color: #884d00;
          border: 1px solid #ffe0b2;
        `;
      case 'info':
      default:
        return `
          background-color: rgba(227, 242, 253, 0.95);
          color: #0d47a1;
          border: 1px solid #bbdefb;
        `;
    }
  }}
`;

const MessageText = styled.span`
  flex-grow: 1;
  margin-right: 10px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: inherit;
  padding: 0;
  line-height: 1;
`;

const AlertCustom: React.FC<AlertCustomProps> = ({ message, onClose, type = 'info' }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <AlertWrapper>
      <AlertContent type={type}>
        <MessageText>{message}</MessageText>
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </AlertContent>
    </AlertWrapper>
  );
}

export default AlertCustom;